import { FC, useMemo } from 'react';
import { LocalizedResourceDictionaryKeys } from 'src/application/localisation/i18next';
import { IFormControlsResolver } from 'src/features/forms/base/controls/IFormControlsResolver';
import { ICheckBoxGroupInput } from 'src/features/forms/base/controls/inputs/CheckBoxGroupInput';
import { ICheckBoxInput } from 'src/features/forms/base/controls/inputs/CheckBoxInput';
import { IDateInput } from 'src/features/forms/base/controls/inputs/DateInput';
import { IDateRangeInput } from 'src/features/forms/base/controls/inputs/DateRangeInput';
import { IRadioInput } from 'src/features/forms/base/controls/inputs/RadioInput';
import { ISelectInput } from 'src/features/forms/base/controls/inputs/SelectInput';
import { ITextInput } from 'src/features/forms/base/controls/inputs/TextInput';
import { ITimeInput } from 'src/features/forms/base/controls/inputs/TimeInput';
import { ICalculatedInput } from 'src/features/forms/base/controls/specials/CalculatedBlock';
import { IPatientGroupInput } from 'src/features/forms/base/controls/specials/PatientGroupInput';
import { formatDateRange, localFormat, millsToTimeString, toDateOrNull } from 'src/helpers/date-helpers';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { useFieldDataFromUiEditorContext } from '../../controls/Inputs/base/hooks';
import { ITagInput } from 'src/features/forms/base/controls/inputs/TagInputProps';
import { IFilesInput } from 'src/features/forms/base/controls/inputs/Files';
import { useContextSelector } from 'use-context-selector';
import { FormFillContext } from '../../uiEditor/provider/formFill.context';
import { useTranslation } from 'react-i18next';
import { IFormFieldOption } from 'src/features/forms/base/controls/inputs/base/FormFieldWithOptionsProps';
import { AppAttachmentValue } from 'uikit/fields/attachments/attachmentsField/appAttachmentsField';

const GeneralValueComponent: FC<{ value: string | undefined }> = ({ value }) => {
  return <div style={{ whiteSpace: 'pre-line' }}>{value ?? ''}</div>;
};

const DateField: IDateInput = ({ dataKey }) => {
  const { singleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  const date = toDateOrNull(singleInputValue);
  return <GeneralValueComponent value={(date && localFormat(date))?.toString() ?? undefined} />;
};

const DateRangeField: IDateRangeInput = ({ dataKey }) => {
  const inputValueStartDate = useContextSelector(
    FormFillContext,
    (v) => v.values?.[`${dataKey}_from`] as string | undefined,
  );
  const inputValueEndDate = useContextSelector(
    FormFillContext,
    (v) => v.values?.[`${dataKey}_until`] as string | undefined,
  );
  return (
    <GeneralValueComponent
      value={formatDateRange(toDateOrNull(inputValueStartDate), toDateOrNull(inputValueEndDate))}
    />
  );
};

const TimeField: ITimeInput = ({ dataKey }) => {
  const { singleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  return <GeneralValueComponent value={singleInputValue ? millsToTimeString(+singleInputValue) : ''} />;
};

const TextField: ITextInput = ({ dataKey, units }) => {
  const { singleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  return <GeneralValueComponent value={singleInputValue ? `${singleInputValue} ${units ?? ''}` : ''} />;
};

const CalculatedField: ICalculatedInput = ({ dataKey, units }) => {
  const { singleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  return <GeneralValueComponent value={`${singleInputValue ?? ''} ${units ?? ''}`} />;
};

const CheckBoxField: ICheckBoxInput = ({ dataKey }) => {
  const { t } = useTranslation();
  const { singleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  return (
    <GeneralValueComponent
      value={t(`Forms.Controls.checkBox_${singleInputValue}` as LocalizedResourceDictionaryKeys)}
    />
  );
};

const SingleInputField: IRadioInput = ({ dataKey, options }) => {
  const { singleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  const displayValue = useMemo(() => {
    return options
      .filter((x) => x.value === singleInputValue)
      .map((x) => ({
        key: x.key,
        displayValue: x.displayValue,
      }))[0];
  }, [options, singleInputValue]);

  return <GeneralValueComponent value={displayValue.displayValue} />;
};

const PatientGroupField: IPatientGroupInput = ({ dataKey }) => {
  const { singleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  const study = useStudy();
  const options = useMemo(
    () =>
      study?.groups.map((group) => {
        return {
          key: group.id,
          value: group.id.toString(),
          displayValue: group.title,
        } as IFormFieldOption;
      }) ?? [],
    [study?.groups],
  );
  const displayValue = useMemo(
    () => options?.find((x) => x.value === singleInputValue)?.displayValue,
    [options, singleInputValue],
  );

  return <GeneralValueComponent value={displayValue} />;
};

const MultipleInputField: ICheckBoxGroupInput = ({ dataKey, options }) => {
  const { multipleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  const displayValues = useMemo(() => {
    return options.filter((x) => multipleInputValue?.includes(x.value)).map((x) => x.displayValue);
  }, [options, multipleInputValue]);

  return <GeneralValueComponent value={displayValues.join(', ')} />;
};

const TagField: ITagInput = ({ dataKey }) => {
  const { multipleInputValue } = useFieldDataFromUiEditorContext(dataKey);
  return <GeneralValueComponent value={multipleInputValue?.join(', ') || ''} />;
};

const FilesField: IFilesInput = ({ dataKey }) => {
  const inputValue = useContextSelector(FormFillContext, (v) =>
    v.values ? (v.values[dataKey] as AppAttachmentValue[]) : [],
  );
  return <GeneralValueComponent value={inputValue.map((x) => x.fileName).join(', ')} />;
};

const SelectField: ISelectInput = (props) => {
  return props.isMulti ? <MultipleInputField {...props} /> : <SingleInputField {...props} />;
};

export const StubContent = () => <></>;

export const displayFieldResolver: IFormControlsResolver = {
  EmptyBlock: StubContent,
  Container: StubContent,
  FieldGroupContainer: StubContent,
  Text: StubContent,
  AttentionText: StubContent,
  Title: StubContent,
  StepContainer: StubContent,
  TabsContainer: StubContent,
  ImageBlock: StubContent,
  BmiInputV2: StubContent,
  RecordResultList: StubContent,
  DateIntervalCalc: StubContent,

  DateInput: DateField,
  DateRangeInput: DateRangeField,
  TextInput: TextField,
  NumberInput: TextField,
  SliderInput: TextField,
  BmiInput: TextField,
  TimeInput: TimeField,
  CalculatedBlock: CalculatedField,
  CheckBoxInput: CheckBoxField,
  RadioInput: SingleInputField,
  GroupInput: PatientGroupField,
  SelectInput: SelectField,
  CheckBoxGroupInput: MultipleInputField,
  TagInput: TagField,
  Files: FilesField,
};
